// Conversation service for handling message fetching and processing
import { MessageWithCitations } from './chatTypes';
import { retrieveFile } from './fileDatabase';
import config from '../../config';

const BASE_URL = config.apiUrl;

export interface FetchMessagesParams {
  conversationId: number;
  conversationUuid: string;
  cursor?: string | null;
  direction?: 'next' | 'previous';
  pageSize?: number;
  shouldAutoScroll?: boolean;
}

export interface FetchMessagesResult {
  messages: MessageWithCitations[];
  paginationData: {
    nextCursor: string | null;
    previousCursor: string | null;
    hasNext: boolean;
    hasPrevious: boolean;
  };
  scrollData?: {
    previousScrollHeight: number;
    previousScrollTop: number;
  };
}

export interface FetchMessagesCallbacks {
  onStart: () => void;
  onFinish: () => void;
  onError: (error: any) => void;
  onUnauthorized: () => void;
}

// Attachments live in the SENDER's IndexedDB, never on the server, so a shared
// (team) conversation opened by another member can never resolve them. Rather
// than leaving a raw tag or a dangling blob reference in the bubble, the tag is
// swapped for this plain-text note. Only ever used when the local lookup FAILS;
// a successful lookup behaves exactly as before.
const missingAttachmentNote = (name: string): string =>
  `[Lampiran "${name}" hanya tersedia di perangkat pengirim]`;

// Helper function to process message files
async function processMessageFiles(messages: any[]): Promise<MessageWithCitations[]> {
  return await Promise.all(
    messages.map(async (rawMessage) => {
      // Every message in this function came from the conversations API, so its
      // `id` is a real server id - mark it. Feedback (and anything else that
      // must hit a server message id) gates on this flag; client placeholders
      // created during streaming use Date.now() ids and never get it.
      const message = { ...rawMessage, fromServer: true } as any;
      // Server now returns persisted billing per assistant message (joined
      // from ai_metrics) - map it to the client-side fields so the credits
      // label + model-breakdown tooltip survive page reloads.
      if (typeof rawMessage.credits_charged === 'number' && rawMessage.credits_charged > 0) {
        message.creditsCharged = rawMessage.credits_charged;
      }
      if (Array.isArray(rawMessage.models_used) && rawMessage.models_used.length > 0) {
        message.modelsUsed = rawMessage.models_used;
      }
      // Persisted tokens/sec estimate → the meta row shows "~N tok/s" on reloaded
      // messages, matching the live streamed row (which uses the client-side tps).
      if (typeof rawMessage.tps_est === 'number' && rawMessage.tps_est > 0) {
        message.tpsEst = rawMessage.tps_est;
      }
      // Persisted research trace (joined from ai_metrics) → the client-side
      // steps/traceDurationMs shape the completed TraceCard already renders.
      // Guard: never clobber richer client-side steps (a just-finished session
      // message gets its live trace re-attached by reattachTrace, which runs
      // after this and wins regardless).
      if (
        !message.steps &&
        rawMessage.trace &&
        Array.isArray(rawMessage.trace.steps) &&
        rawMessage.trace.steps.length > 0
      ) {
        const steps = rawMessage.trace.steps
          .filter((s: any) => s && typeof s.label === 'string' && s.label)
          .map((s: any, i: number) => ({
            step_id: i + 1,
            label: s.label,
            ...(typeof s.detail === 'string' && s.detail ? { detail: s.detail } : {}),
            status: 'done' as const,
          }));
        if (steps.length > 0) {
          message.steps = steps;
          if (typeof rawMessage.trace.duration_ms === 'number' && rawMessage.trace.duration_ms > 0) {
            message.traceDurationMs = rawMessage.trace.duration_ms;
          }
        }
      }
      console.log('Processing message:', {
        role: message.role,
        content: message.content,
        model: message.model
      });

      // Handle [image] tags
      if (message.content.includes('[image]')) {
        const imageMatch = message.content.match(/\[image\](.*?)\[\/image\]/);
        if (imageMatch) {
          const filename = imageMatch[1];
          console.log('Found image tag with filename:', filename);
          const file = await retrieveFile(filename);
          console.log('Retrieved file for image:', file ? 'Found' : 'Not found');
          if (file) {
            console.log('Creating URL for image file:', filename);
            return {
              ...message,
              fileUrl: filename
            };
          }
          // Not in THIS browser's IndexedDB (shared conversation, other device,
          // cleared storage) - leave a readable note instead of the raw tag.
          return {
            ...message,
            content: message.content.replace(imageMatch[0], missingAttachmentNote(filename)),
          };
        }
      }

      // Handle [File:] tags - support multiple files
      if (message.content.includes('[File:')) {
        // Find all file references in the content using a more robust regex
        const fileMatches = message.content.match(/\[File: ([^\]]+)\](?:\(file:([^)]+)\))?/g);
        
        if (fileMatches && fileMatches.length > 0) {
          const fileUrls: string[] = [];
          const files: { file: File; filename: string }[] = [];
          // Tags whose blob is not in THIS browser: swapped for a note below.
          const missingTags: { tag: string; displayName: string }[] = [];

          for (const fileMatch of fileMatches) {
            // Extract filename from the matched string
            const fileInfo = fileMatch.match(/\[File: ([^\]]+)\](?:\(file:([^)]+)\))?/);
            if (fileInfo) {
              // Use the filename from the file:() part if available, otherwise use the display name
              const filename = fileInfo[2] || fileInfo[1];
              console.log('Found file tag with filename:', filename);
              const file = await retrieveFile(filename);
              console.log('Retrieved file:', file ? 'Found' : 'Not found');
              if (file) {
                console.log('Creating URL for file:', filename);
                const url = URL.createObjectURL(file);
                fileUrls.push(url);
                files.push({ file, filename });
              } else {
                missingTags.push({ tag: fileMatch, displayName: fileInfo[1] });
              }
            }
          }

          // Unresolvable attachments (another member's device, cleared storage)
          // read as a note rather than a raw tag / broken preview.
          let content = message.content;
          for (const miss of missingTags) {
            content = content.replace(miss.tag, missingAttachmentNote(miss.displayName));
          }

          if (fileUrls.length > 0) {
            console.log(`Processed ${fileUrls.length} files for message:`, fileUrls.map((_, i) => files[i].filename));
            return {
              ...message,
              content,
              fileUrl: fileUrls[0], // Keep for backward compatibility
              fileUrls: fileUrls, // Add multiple files support
              fileData: files // Add file metadata
            };
          }

          if (missingTags.length > 0) {
            return { ...message, content };
          }
        }
      }
      
      return message;
    })
  );
}

export async function fetchConversationMessages(
  params: FetchMessagesParams,
  token: string,
  tokenType: string,
  callbacks: FetchMessagesCallbacks,
  chatContainerRef?: React.RefObject<HTMLDivElement>
): Promise<FetchMessagesResult | null> {
  const {
    conversationUuid,
    cursor,
    direction = 'next',
    pageSize = 10
  } = params;

  // Get scroll position before making request
  const scrollContainer = chatContainerRef?.current;
  const previousScrollHeight = scrollContainer?.scrollHeight || 0;
  const previousScrollTop = scrollContainer?.scrollTop || 0;

  callbacks.onStart();

  try {
    const url = new URL(`${BASE_URL}/conversations/uuid/${conversationUuid}/messages`);
    
    const queryParams = {
      page_size: pageSize.toString(),
      direction,
      ...(cursor && { cursor })
    };

    Object.entries(queryParams).forEach(([key, value]) => {
      url.searchParams.append(key, value);
    });

    const response = await fetch(url.toString(), {
      headers: {
        'Authorization': `${tokenType} ${token}`
      }
    });
    
    // Handle 401 Unauthorized
    if (response.status === 401) {
      console.log('401 Unauthorized, redirecting to login');
      localStorage.clear();
      callbacks.onUnauthorized();
      return null;
    }
    
    if (response.ok) {
      const data = await response.json();
      const orderedMessages = [...data.messages].reverse();
      
      // Process messages to restore file URLs
      const processedMessages = await processMessageFiles(orderedMessages);
      
      const result: FetchMessagesResult = {
        messages: processedMessages,
        paginationData: {
          nextCursor: data.next_cursor,
          previousCursor: data.previous_cursor,
          hasNext: data.has_next,
          hasPrevious: data.has_previous
        },
        scrollData: {
          previousScrollHeight,
          previousScrollTop
        }
      };

      callbacks.onFinish();
      return result;
    } else {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  } catch (error) {
    console.error('Error fetching conversation messages:', error);
    callbacks.onError(error);
    callbacks.onFinish();
    return null;
  }
}

// Helper function to handle scroll position restoration
export function restoreScrollPosition(
  scrollContainer: HTMLDivElement,
  scrollData: { previousScrollHeight: number; previousScrollTop: number }
) {
  const { previousScrollHeight, previousScrollTop } = scrollData;
  
  const maintainScroll = () => {
    const newScrollHeight = scrollContainer.scrollHeight;
    const scrollDiff = newScrollHeight - previousScrollHeight;
    scrollContainer.scrollTop = previousScrollTop + scrollDiff;
  };

  // Try multiple times to ensure the scroll position is maintained
  maintainScroll();
  requestAnimationFrame(maintainScroll);
  setTimeout(maintainScroll, 50);
}